'use client';

import { useState, type ReactNode } from 'react';

import { AnimatePresence, cn, motion } from '@/libs';

import { Icon } from '../Icon';

type AccordionProps = {
  headerContent: ReactNode;
  children?: ReactNode;
  footerContent?: ReactNode;
  headerClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  className?: string;
  defaultOpen?: boolean;
  showArrow?: boolean;
  showLine?: boolean;
  onToggle?: (open: boolean) => void;
};

export const Accordion = ({
  headerContent,
  children,
  footerContent,
  headerClassName,
  contentClassName,
  footerClassName,
  className,
  defaultOpen = false,
  showArrow = true,
  showLine = true,
  onToggle,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  return (
    <section data-open={isOpen ? 'true' : 'false'} className={cn('text-xs', className)}>
      <div
        className={cn(
          'relative cursor-pointer',
          isOpen &&
            showLine &&
            "before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[1px] before:bg-foreground/10",
          headerClassName,
        )}
        onClick={handleToggle}
      >
        <div className="relative flex items-center justify-between w-full">
          <div className="flex-1 text-sm font-semibold break-words">{headerContent}</div>
          {showArrow && (
            <Icon.ChevronDown
              width={12}
              height={12}
              className={cn(
                'absolute my-auto top-0 bottom-0 right-4 transition-transform pointer-events-none flex-shrink-0',
                isOpen && '-rotate-180',
              )}
            />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className={cn('overflow-hidden', contentClassName)}
            initial={{
              height: 0,
              opacity: 0,
              maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            }}
            animate={{
              height: 'auto',
              opacity: 1,
              maskImage: 'linear-gradient(to bottom, black 100%, transparent 100%)',
            }}
            exit={{
              height: 0,
              opacity: 0,
              maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            }}
            transition={{ type: 'spring', visualDuration: 0.2, bounce: 0 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      {footerContent && (
        <div
          className={cn(
            'relative flex items-center justify-between px-3 py-2 cursor-pointer',
            "before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-[1px] before:bg-foreground/10",
            footerClassName,
          )}
          onClick={handleToggle}
        >
          {footerContent}
        </div>
      )}
    </section>
  );
};
